import { Banknote, Building2, Calendar, Clock, Mail, Phone, TrendingUp, User, MoreHorizontal, ToggleLeft, ToggleRight } from "lucide-react";

import ActionMenu from "../../../components/shared/ActionMenu";
import CurrencyDisplay from "../../../components/shared/CurrencyDisplay";
import DataTable from "../../../components/shared/DataTable";
import TruncatedText from "../../../components/shared/TruncatedText";
import { Badge } from "../../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { Switch } from "../../../components/ui/switch.jsx";
import { Label } from "../../../components/ui/label.jsx";
import { formatDate } from "../../../utils/dateUtils";

const LeadTable = ({
  leads,
  onEdit,
  onDelete,
  onStatusUpdate,
  getPriorityColor,
  getStatusColor,
  permissions,
  sortBy,
  direction,
  onSort,
  showDeleted,
  setShowDeleted,
  setCurrentPage
}) => {
  const leadColumns = [
    {
      header: "Lead",
      accessor: "title",
      isPrimary: true,
      render: (val, lead) => (
        <div className="flex items-center gap-3">
          <div className="p-2 bg-yale-blue-50 dark:bg-yale-blue-800 rounded-lg shrink-0">
            <Building2 className="w-5 h-5 text-yale-blue-600 dark:text-yale-blue-400" />
          </div>
          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <TruncatedText
                text={lead.title}
                maxLength={25}
                className="text-sm font-semibold text-yale-blue-900 dark:text-slate-100"
              />
              {lead.deleted && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-extrabold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 uppercase tracking-wider shrink-0">
                  Deleted
                </span>
              )}
            </div>
            <span className="text-[10px] text-slate-500 uppercase tracking-wider truncate max-w-[180px]">
              {lead.companyName || "No Company"}
            </span>
          </div>
        </div>
      )
    },
    {
      header: "Contact",
      accessor: "contactPerson",
      render: (val, lead) => (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5 text-sm text-slate-700 dark:text-slate-300 font-medium">
            <User className="w-3.5 h-3.5 text-yale-blue-500" />
            {val || "N/A"}
          </div>
          <div className="flex items-center gap-3">
            {lead.phone && (
              <div className="flex items-center gap-1 text-[11px] text-slate-500">
                <Phone className="w-3 h-3" />
                {lead.phone}
              </div>
            )}
            {lead.email && (
              <div className="flex items-center gap-1 text-[11px] text-slate-500">
                <Mail className="w-3 h-3" />
                {lead.email}
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      header: "Value",
      accessor: "estimatedValue",
      render: (val) => (
        <div className="flex items-center gap-1.5 text-sm font-semibold text-green-600 dark:text-green-400">
          <Banknote className="w-3.5 h-3.5" />
          <CurrencyDisplay amount={val} />
        </div>
      )
    },
    {
      header: "Status",
      accessor: "status",
      render: (val, lead) => (
        <div className="flex items-center gap-2">
          <Select value={val} onValueChange={(newStatus) => onStatusUpdate(lead.id, newStatus)}>
            <SelectTrigger
              className={`h-8 px-3 rounded-full border border-slate-200 dark:border-yale-blue-800 bg-white dark:bg-yale-blue-900 hover:border-yale-blue-400 dark:hover:border-yale-blue-600 transition-all w-[160px]`}
            >
              <SelectValue>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${getStatusColor(val).split(" ")[0]}`} />
                  <span className="text-xs font-medium capitalize">{val.replace("_", " ")}</span>
                </div>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="new" className="text-xs capitalize">
                New
              </SelectItem>
              <SelectItem value="contacted" className="text-xs capitalize">
                Contacted
              </SelectItem>
              <SelectItem value="meeting_scheduled" className="text-xs capitalize">
                Meeting Scheduled
              </SelectItem>
              <SelectItem value="proposal_sent" className="text-xs capitalize">
                Proposal Sent
              </SelectItem>
              <SelectItem value="won" className="text-xs capitalize">
                Won
              </SelectItem>
              <SelectItem value="lost" className="text-xs capitalize">
                Lost
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      )
    },
    {
      header: "Priority",
      accessor: "priority",
      render: (val) => (
        <Badge variant="outline" className={`${getPriorityColor(val)} border-opacity-50 uppercase text-[10px]`}>
          {val}
        </Badge>
      )
    },
    {
      header: "Next Action",
      accessor: "meetingDate",
      sortable: true,
      sortKey: "meetingDate",
      render: (val, lead) => (
        <div className="flex flex-col gap-1">
          {val ? (
            <div className="flex items-center gap-1.5 text-xs text-yale-blue-600 dark:text-yale-blue-400 font-medium">
              <Calendar className="w-3.5 h-3.5" />
              {formatDate(val)}
            </div>
          ) : (
            <span className="text-[11px] text-slate-400 italic">No action set</span>
          )}
          {lead.nextAction && (
            <div className="flex items-center gap-1.5 mt-0.5">
              <Clock className="w-3 h-3 text-slate-400 shrink-0" />
              <TruncatedText text={lead.nextAction} maxLength={30} className="text-[11px] text-slate-500 italic" />
            </div>
          )}
        </div>
      )
    },
    {
      header: "Actions",
      filter: permissions?.canDelete ? (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${showDeleted ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="w-4 h-4 hidden md:block" />
              <span className="md:hidden">
                {showDeleted ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-3" align="end">
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <Switch
                checked={showDeleted}
                onCheckedChange={(val) => {
                  setShowDeleted(val);
                  setCurrentPage(0);
                }}
                id="show-deleted-leads-table"
              />
              <Label htmlFor="show-deleted-leads-table" className="text-xs font-semibold cursor-pointer whitespace-nowrap">
                Show Deleted
              </Label>
            </div>
          </PopoverContent>
        </Popover>
      ) : null,
      accessor: "id",
      render: (_, lead) => (
        <ActionMenu
          canEdit={permissions?.canEdit}
          canDelete={permissions?.canDelete}
          onEdit={() => onEdit(lead)}
          onDelete={() => onDelete(lead.id)}
        />
      )
    }
  ];

  return (
    <DataTable
      columns={leadColumns}
      data={leads}
      emptyStateTitle="No leads found"
      emptyStateDescription="Start by adding your first lead opportunity to track business growth."
      emptyStateIcon={TrendingUp}
      sortBy={sortBy}
      direction={direction}
      onSort={onSort}
    />
  );
};

export default LeadTable;
